'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  BikeRegistrationInputModal,
  type BikeRegistrationInputModalPresentation,
} from '../../lp/bike-insurance/BikeRegistrationInputModal'
import { SimpleBikeFlowModal } from '../../lp/bike-insurance/SimpleBikeFlowModal'
import { getHeroContentViewModel } from '../../lp/bike-insurance/heroWidgetBehavior.mjs'
import { useBikeRegistrationFlow } from '../../lp/bike-insurance/useBikeRegistrationFlow'
import { StickyFooterClient } from '../StickyFooterClient'
import { trackComprehensiveEngagement } from './ComprehensiveAnalyticsClient'
import { CTASection } from './CTASection'
import { COMPREHENSIVE_HERO_CONTENT } from './heroConfig'

interface ComprehensiveBikeInsuranceFlowClientProps {
  regexPatternsData: string[]
}

export const ComprehensiveBikeInsuranceFlowClient = ({
  regexPatternsData,
}: ComprehensiveBikeInsuranceFlowClientProps) => {
  const [showInputModal, setShowInputModal] = useState(false)
  const [inputModalPresentation, setInputModalPresentation] = useState<BikeRegistrationInputModalPresentation>('modal')
  const heroViewModel = useMemo(() => getHeroContentViewModel(COMPREHENSIVE_HERO_CONTENT), [])
  const registrationFlow = useBikeRegistrationFlow({
    ctaText: heroViewModel.widget.ctaText,
    isSemPage: false,
    regexPatternsData,
    segmentHeading: heroViewModel.heading,
  })

  useEffect(() => {
    if (registrationFlow?.showIllogicalFlowModal) {
      setShowInputModal(false)
    }
  }, [registrationFlow?.showIllogicalFlowModal])

  useEffect(() => {
    document.documentElement.style.overflowY =
      showInputModal || registrationFlow.showIllogicalFlowModal ? 'clip' : 'scroll'

    return () => {
      document.documentElement.style.overflowY = 'scroll'
    }
  }, [registrationFlow.showIllogicalFlowModal, showInputModal])

  const openInputModal = (
    presentation: BikeRegistrationInputModalPresentation,
    componentName: 'fixed_widget' | 'floating_widget'
  ) => {
    void trackComprehensiveEngagement(componentName, heroViewModel.widget.ctaText)
    setInputModalPresentation(presentation)
    setShowInputModal(true)
  }

  const handleEditRegistrationFromFlowModal = () => {
    registrationFlow?.resetIllogicalFlow()
    setShowInputModal(true)
  }

  const registrationFormProps = {
    ctaText: heroViewModel.widget.ctaText,
    disclaimer: heroViewModel.widget.disclaimer,
    heading: heroViewModel.widget.heading,
    inputPlaceholder: heroViewModel.widget.inputPlaceholder,
    isFetching: registrationFlow.isFetching,
    onBlur: registrationFlow.handleRegistrationNumberBlur,
    onChange: registrationFlow.handleRegistrationNumberChange,
    onSubmit: registrationFlow.fetchAndInvokeHandleSubmit,
    registrationError: registrationFlow.registrationError,
    regNumber: registrationFlow.regNumber,
    showError: registrationFlow.showError,
  }

  return (
    <>
      <CTASection
        onCheckPriceClick={() => openInputModal(window.innerWidth < 768 ? 'bottom-sheet' : 'modal', 'fixed_widget')}
      />
      <StickyFooterClient
        triggerId="mobile-sticky-footer-trigger"
        priceText="Starting at ₹475/year*"
        priceScale="sm"
        showOnDesktop
        onCheckPriceClick={() => openInputModal('bottom-sheet', 'floating_widget')}
      />
      <BikeRegistrationInputModal
        show={showInputModal}
        onClose={() => setShowInputModal(false)}
        presentation={inputModalPresentation}
        registrationFormProps={registrationFormProps}
        onNewBikeClick={registrationFlow.handleNewBikeClick}
        viewModel={heroViewModel}
      />
      <SimpleBikeFlowModal
        show={registrationFlow.showIllogicalFlowModal}
        regNo={registrationFlow.regNumber}
        errorCode={registrationFlow.proposalAPIResponse?.error_code}
        illogicalFlowData={registrationFlow.proposalAPIResponse?.error_context}
        product={registrationFlow.product}
        setProposalAPIResponse={registrationFlow.setProposalAPIResponse}
        setShowIllogicalFlowModal={registrationFlow.setShowIllogicalFlowModal}
        onClose={registrationFlow.resetIllogicalFlow}
        onEditRegistration={handleEditRegistrationFromFlowModal}
      />
    </>
  )
}
